import { useEffect, useRef, useState } from 'react';

import { hasLikelySmartSearchIntent } from '@cfa/pathway-fe-shared';
import {
  AmplitudeProductEvent,
  type AmplitudeProductEventPayload,
} from '@cfa/pathway-fe-shared';

import {
  createEngagedTimer,
  DEFAULT_IDLE_TIMEOUT_MS,
  type SessionEndReason,
  systemNow,
} from '@/analytics/engagedTimer';

export type TrackFn = (
  event: typeof AmplitudeProductEvent.SEARCH_TIME_SPENT,
  properties: AmplitudeProductEventPayload<
    typeof AmplitudeProductEvent.SEARCH_TIME_SPENT
  >,
) => void;

export interface SearchSessionState {
  active: boolean;
  timerStartedAt: number | null;
  accumulatedActiveMs: number;
  searchCount: number;
  resourceClicks: number;
}

export interface UseSearchSessionOptions {
  smartSearchEnabled: boolean;
  track: TrackFn;
  idleTimeoutMs?: number;
}

export interface UseSearchSessionReturn {
  activate: () => void;
  deactivate: () => void;
  pause: () => void;
  resume: () => void;
  fireAndReset: (endReason?: SessionEndReason) => void;
  incrementSearchCount: () => void;
  noteSearchSubmitted: (query: string) => void;
  incrementResourceClicks: () => void;
  getState: () => SearchSessionState;
  enterResourceView: () => void;
  exitResourceView: () => void;
  reportActivity: () => void;
  pauseForHidden: () => void;
  resumeFromHidden: () => void;
  noteSurface: (segment: string) => void;
}

/**
 * Search adapter around `createEngagedTimer`.
 *
 * Refs are the mutable session bag (timer engine, counts, idle handle, trackRef /
 * smartSearchRef for fire). They are not an extra state layer: nothing here should re-render
 * on tick/activity. React Compiler memoizes the returned methods — no
 * useCallback/useMemo.
 */
export const useSearchSession = ({
  smartSearchEnabled,
  track,
  idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS,
}: UseSearchSessionOptions): UseSearchSessionReturn => {
  const trackRef = useRef(track);
  const smartSearchRef = useRef(smartSearchEnabled);
  useEffect(() => {
    trackRef.current = track;
    smartSearchRef.current = smartSearchEnabled;
  }, [track, smartSearchEnabled]);

  const [engine] = useState(() =>
    createEngagedTimer({ idleTimeoutMs, now: systemNow }),
  );
  const searchCountRef = useRef(0);
  const resourceClicksRef = useRef(0);
  const smartSearchIntentRef = useRef(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearIdleTimer = () => {
    if (idleTimerRef.current !== null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  };

  const startIdleTimer = () => {
    clearIdleTimer();
    if (!engine.isIdleWatchActive() || engine.isHiddenPaused()) {
      return;
    }
    idleTimerRef.current = setTimeout(() => {
      engine.pause('idle');
    }, idleTimeoutMs);
  };

  const fire = (endReason: SessionEndReason) => {
    if (!engine.isActive() || searchCountRef.current === 0) {
      return;
    }

    const journey = engine.consumeForFire(endReason);
    if (journey.duration_seconds < 1) {
      searchCountRef.current = 0;
      resourceClicksRef.current = 0;
      smartSearchIntentRef.current = false;
      return;
    }

    trackRef.current(AmplitudeProductEvent.SEARCH_TIME_SPENT, {
      ...journey,
      search_count: searchCountRef.current,
      resources_clicked: resourceClicksRef.current,
      smart_search_enabled: smartSearchRef.current,
      smart_search_intent: smartSearchIntentRef.current,
    });

    searchCountRef.current = 0;
    resourceClicksRef.current = 0;
    smartSearchIntentRef.current = false;
  };

  const resetSession = () => {
    clearIdleTimer();
    engine.reset();
    searchCountRef.current = 0;
    resourceClicksRef.current = 0;
    smartSearchIntentRef.current = false;
  };

  useEffect(
    () => () => {
      clearIdleTimer();
      if (!engine.isActive() || searchCountRef.current === 0) {
        return;
      }

      const journey = engine.consumeForFire('unmount');
      if (journey.duration_seconds < 1) {
        searchCountRef.current = 0;
        resourceClicksRef.current = 0;
        smartSearchIntentRef.current = false;
        return;
      }

      trackRef.current(AmplitudeProductEvent.SEARCH_TIME_SPENT, {
        ...journey,
        search_count: searchCountRef.current,
        resources_clicked: resourceClicksRef.current,
        smart_search_enabled: smartSearchRef.current,
        smart_search_intent: smartSearchIntentRef.current,
      });

      searchCountRef.current = 0;
      resourceClicksRef.current = 0;
      smartSearchIntentRef.current = false;
    },
    [engine],
  );

  return {
    activate: () => {
      if (!engine.isActive()) {
        engine.activate();
      }
    },
    deactivate: () => {
      if (!engine.isActive()) {
        return;
      }
      clearIdleTimer();
      if (searchCountRef.current > 0) {
        fire('navigate_away');
        return;
      }
      resetSession();
    },
    pause: () => {
      engine.pause();
    },
    resume: () => {
      engine.resume();
    },
    fireAndReset: (endReason: SessionEndReason = 'navigate_away') => {
      clearIdleTimer();
      if (engine.isActive() && searchCountRef.current > 0) {
        fire(endReason);
      } else {
        resetSession();
      }
    },
    incrementSearchCount: () => {
      searchCountRef.current += 1;
    },
    noteSearchSubmitted: (query: string) => {
      searchCountRef.current += 1;
      smartSearchIntentRef.current = hasLikelySmartSearchIntent(query);
    },
    incrementResourceClicks: () => {
      resourceClicksRef.current += 1;
    },
    getState: () => ({
      active: engine.isActive(),
      timerStartedAt: engine.getTimerStartedAt(),
      accumulatedActiveMs: engine.getAccumulatedActiveMs(),
      searchCount: searchCountRef.current,
      resourceClicks: resourceClicksRef.current,
    }),
    enterResourceView: () => {
      engine.enterForegroundWatch();
      startIdleTimer();
    },
    exitResourceView: () => {
      clearIdleTimer();
      engine.exitForegroundWatch();
    },
    reportActivity: () => {
      engine.reportActivity();
      startIdleTimer();
    },
    pauseForHidden: () => {
      clearIdleTimer();
      engine.pause('hidden');
    },
    resumeFromHidden: () => {
      engine.resumeFromHidden();
      startIdleTimer();
    },
    noteSurface: (segment: string) => {
      engine.noteSurface(segment);
    },
  };
};
